import React, {useEffect, useMemo, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {useFormLineCalculations} from 'components/pages/DocumentsCreate/InvoiceNew/hooks/useFormLineCalculations';
import {useLineCalculations} from 'components/pages/DocumentsCreate/InvoiceNew/hooks/useLineCalculations';
import {UniController} from 'components/pages/DocumentsCreate/InvoiceNew/universalController';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {Modal} from 'components/ui-kit/Modal';
import {CURRENCY} from 'core/constants/currency';
import {
    $invoiceLineChargeData,
    $invoiceLinesHolder,
    setInvoiceLineChargeData,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';

import {
    $inlineInvoiceChargeModal,
    $inlineModalFormReadonly,
    setInlineInvoiceChargeModal,
    setInlineModalFormReadonly,
} from 'models/documents/invoices/details';

import {createIsFieldRequired, createValidationContext, chargesModalSchema} from '../../validation';

import {ButtonWrapper, Grid} from './styled';

export const ModalFormReadonly = () => {
    const ivoiceLinesHolder = useStore($invoiceLinesHolder);
    const inlineModalFormReadonly = useStore($inlineModalFormReadonly);
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});

    const setModalData = useMemo(() => {
        const chargesData = ivoiceLinesHolder[inlineModalFormReadonly.index]?.charges;
        if (chargesData) {
            return (
                <Grid columns={'1fr 1fr'}>
                    <div style={{gridColumn: '1/-1'}}>
                        {chargesData.isCharge === 'true' ? t(`radio.1`) : t(`radio.2`)}
                    </div>
                    <div style={{gridColumn: '1/-1'}}>
                        <Input
                            type={'text'}
                            label={t('reason.label')}
                            disabled={true}
                            value={chargesData.reason}
                        />
                    </div>
                    {chargesData.discountRateAndBaseAmount && (
                        <>
                            <Input
                                type={'text'}
                                label={t('rate')}
                                disabled={true}
                                value={chargesData.ratePercent}
                            />
                            <Input
                                type={'text'}
                                label={t('baseAmount.label')}
                                disabled={true}
                                value={chargesData.baseAmount}
                            />
                        </>
                    )}
                    <div style={{gridColumn: '1/-1'}}>
                        <Input
                            type={'text'}
                            label={t('amount', {currency: CURRENCY})}
                            disabled={true}
                            value={chargesData.amountXaf}
                        />
                    </div>
                </Grid>
            );
        }
    }, [ivoiceLinesHolder, inlineModalFormReadonly]);

    return (
        <Modal
            title={t('charges')}
            show={inlineModalFormReadonly.show}
            modalWidth={0}
            onClose={() =>
                setInlineModalFormReadonly({
                    index: -1,
                    show: false,
                })
            }
        >
            <div>{setModalData}</div>
        </Modal>
    );
};

export const ModalFormComponent = ({
    getValues: externalGetValues,
    setValue: externalSetValue,
    trigger: externalTrigger,
}: {
    getValues?: (x?: string | string[]) => any;
    setValue?: (name: any, value: any) => any;
    trigger?: (name?: any) => Promise<boolean>;
}) => {
    const inlineInvoiceChargeModal = useStore($inlineInvoiceChargeModal);
    const invoiceLineChargeData = useStore($invoiceLineChargeData);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});
    const [extraFileds, setExtraFileds] = useState(false);

    const {index: lineId, show: isShow} = inlineInvoiceChargeModal;
    const formContext = useMemo(() => createValidationContext({extraFileds}), [extraFileds]);
    const isFieldRequired = useMemo(() => createIsFieldRequired(formContext, chargesModalSchema), [
        formContext,
        chargesModalSchema,
    ]);
    type IsFieldRequired = (name: string) => boolean;

    const getRequiredFlag = (isFieldRequired?: IsFieldRequired) => (
        name: string,
        fallback = false,
    ) => (isFieldRequired ? isFieldRequired(name) : fallback);

    const {
        control,
        handleSubmit,
        trigger,
        setValue,
        reset,
        getValues,
        formState: {errors},
    } = useForm({
        mode: 'all',
        resolver: yupResolver(chargesModalSchema),
        context: formContext,
    });

    const lineCalculations = useLineCalculations();
    const formLineCalculations = useFormLineCalculations({
        getValues: externalGetValues,
        setValue: externalSetValue,
        trigger: externalTrigger,
    });

    function onSubmitModal(data: any) {
        lineId !== undefined
            ? updateInvoiceLinesHolder({index: lineId, data: {columnId: 'charges', value: data}})
            : setInvoiceLineChargeData(data);

        const combineValues =
            lineId !== undefined
                ? lineCalculations.combineValues
                : formLineCalculations.combineValues;

        combineValues(lineId);

        setInlineInvoiceChargeModal({
            index: undefined,
            show: false,
        });
        reset();
        setExtraFileds(false);
    }

    useEffect(() => {
        const item =
            lineId === undefined ? invoiceLineChargeData : invoiceLinesHolder[lineId]?.charges;
        if (item) {
            setExtraFileds(item.discountRateAndBaseAmount);
        }
    }, [lineId, invoiceLineChargeData, isShow]);

    const fieldsSettings = useMemo(() => {
        const item =
            lineId === undefined ? invoiceLineChargeData : invoiceLinesHolder[lineId]?.charges;

        const general = [
            {
                type: 'switcher',
                defaultVal: extraFileds,
                label: t('addDiscount'),
                name: 'discountRateAndBaseAmount',
                required: false,
            },
            {
                type: 'radio',
                defaultVal: item?.isCharge ?? 'true',
                options: [
                    {label: t(`radio.1`), value: 'true'},
                    {label: t(`radio.2`), value: 'false'},
                ],
                name: 'isCharge',
                required: false,
            },
            {
                type: 'input',
                defaultVal: item?.reason ?? '',
                label: `${t('reason.label')} ${getRequiredFlag(isFieldRequired)('reason')}`,
                placeholder: t('reason.placeholder'),
                name: 'reason',
                required: getRequiredFlag(isFieldRequired)('reason'),
            },
        ];
        const total = [
            {
                type: 'input',
                defaultVal: item?.amountXaf ?? null,
                rawType: 'number',
                label: t('amount', {currency: CURRENCY}),
                placeholder: t('enterAmount'),
                name: 'amountXaf',
                required: getRequiredFlag(isFieldRequired)('amountXaf'),
                disabled: extraFileds,
            },
        ];

        const extra = [
            {
                type: 'input',
                defaultVal: item?.ratePercent ?? null,
                rawType: 'number',
                label: t('rate'),
                placeholder: t('enterRate'),
                name: 'ratePercent',
                required: getRequiredFlag(isFieldRequired)('ratePercent'),
            },
            {
                type: 'input',
                defaultVal: item?.baseAmount ?? null,
                rawType: 'number',
                label: t('baseAmount.label'),
                placeholder: t('baseAmount.placeholder'),
                name: 'baseAmount',
                required: getRequiredFlag(isFieldRequired)('baseAmount'),
            },
        ];

        return {general, total, extra};
    }, [extraFileds, isShow, lineId, reset]);

    function handleSwitchToggling(x: {name: string; value: unknown}) {
        if (x.name === 'discountRateAndBaseAmount') {
            setExtraFileds(x.value as boolean);
            setValue('ratePercent', null);
            setValue('baseAmount', null);
            setValue('amountXaf', null);
        }
        // trigger();
    }

    function calculateTotal() {
        const {ratePercent, baseAmount} = getValues(['ratePercent', 'baseAmount']);

        if (ratePercent && baseAmount) {
            const amountValue = (Number(baseAmount) / 100) * Number(ratePercent);
            setValue('amountXaf', amountValue);
        } else {
            setValue('amountXaf', null);
        }
        trigger();
    }

    return (
        <Modal
            title={t('charges')}
            show={isShow}
            modalWidth={0}
            onClose={() => {
                reset();
                setExtraFileds(false);
                setInlineInvoiceChargeModal({
                    index: undefined,
                    show: false,
                });
            }}
        >
            <form onSubmit={handleSubmit(onSubmitModal)}>
                <Grid columns={'1fr 1fr'}>
                    {fieldsSettings.general.map((item, index) => (
                        <div key={`outer_${index}_${item.name}}`} style={{gridColumn: '1/-1'}}>
                            <UniController
                                key={`${index}_${item.name}}`}
                                item={item}
                                index={index}
                                errors={errors}
                                control={control}
                                setValue={setValue}
                                trigger={trigger}
                                cb={handleSwitchToggling}
                            />
                        </div>
                    ))}
                    {extraFileds &&
                        fieldsSettings.extra.map((item, index) => (
                            <UniController
                                key={`${index}_${item.name}}`}
                                item={item}
                                index={index}
                                errors={errors}
                                control={control}
                                setValue={setValue}
                                trigger={trigger}
                                cb={calculateTotal}
                            />
                        ))}
                    {fieldsSettings.total.map((item, index) => (
                        <div key={`outer_${index}_${item.name}}`} style={{gridColumn: '1/-1'}}>
                            <UniController
                                key={`${index}_${item.name}}`}
                                item={item}
                                index={index}
                                errors={errors}
                                control={control}
                                setValue={setValue}
                                trigger={trigger}
                                cb={handleSwitchToggling}
                            />
                        </div>
                    ))}
                </Grid>
                <ButtonWrapper>
                    <Button
                        type="button"
                        onClick={() => {
                            setInlineInvoiceChargeModal({
                                index: undefined,
                                show: false,
                            });
                        }}
                        variant={ButtonVariant.Outline}
                    >
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        disabled={!!Object.keys(errors).length}
                        type="submit"
                        variant={ButtonVariant.Normal}
                    >
                        {t('buttons.save')}
                    </Button>
                </ButtonWrapper>
            </form>
        </Modal>
    );
};
